import { useEffect, useRef } from 'react';
import { Box, FlexBox, Typography, getTokenCssVar } from '@vp/swan';
import styled from 'styled-components';
import type { ReactNode } from 'react';
import { __ } from '@99designs/i18n';
import { ChatAssistantAvatar } from '../ChatAssistantAvatar';
import { ChatLoadErrorBanner } from '../ChatLoadErrorBanner';
import { LandingHero } from '../LandingHero';
import { MessageBubble } from '../MessageBubble';
import type { ChatErrorCode } from '../../client/types';
import type { DisplayMessage } from '../../messages';

/** `onReload`'s default before `ChatInterface` threads `useChatHistory`'s real reload through. */
function noop() {
    return undefined;
}

const ThreadScrollArea = styled(Box)`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    position: relative;
    background-color: ${getTokenCssVar('SwanSemColorBgStrong')};
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const MessageLog = styled(Box)`
    max-width: 768px;
    min-height: 100%;
    margin: 0 auto;
    padding: ${getTokenCssVar('SwanSemSpace6')} ${getTokenCssVar('SwanSemSpace5')};
`;

export type ChatThreadProps = {
    /**
     * The whole thread: loaded history plus locally appended messages, already merged
     * by useChat. In-flight and failed messages are indistinguishable here on purpose —
     * the thread renders what was said, and the banners explain what went wrong.
     */
    messages: DisplayMessage[];
    /**
     * Non-null once the initial history load has failed. Presence signal only — never
     * read for display. The copy shown comes from `errorCode` via `ChatLoadErrorBanner`,
     * never from this object's `.message`.
     */
    error: Error | null;
    /**
     * The load failure's contract discriminator, for `ChatLoadErrorBanner` to choose its
     * copy. Optional and defaulted to `null` (the banner's generic-transient copy) so
     * this component still renders correctly before `ChatInterface` threads
     * `useChatHistory`'s `errorCode` through.
     */
    errorCode?: ChatErrorCode | null;
    /** Re-runs the initial history load. Always `useChatHistory`'s `reload` — never a send retry. */
    onReload?: () => void;
    /** Whether the bounded reload budget is spent — swaps the banner to "Refresh page". */
    reloadBudgetExhausted?: boolean;
    /** Whether the assistant is generating a reply. */
    awaitingReply?: boolean;
    /** Key of the one message that should reveal word by word — see `useChatMessages`. */
    latestReplyKey?: string | null;
    /** Host hook for reporting a call-to-action link click. */
    onCtaLinkClick?: (url: string, label: string) => void;
    /** Optional node rendered inline in the log after the last message row. */
    sendErrorBanner?: ReactNode;
};

export function ChatThread({
    messages,
    error,
    errorCode = null,
    onReload = noop,
    reloadBudgetExhausted = false,
    awaitingReply = false,
    latestReplyKey = null,
    onCtaLinkClick,
    sendErrorBanner = null,
}: ChatThreadProps) {
    const endOfThreadRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (messages.length > 0) {
            endOfThreadRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
    }, [messages.length, awaitingReply]);

    return (
        <ThreadScrollArea>
            <LandingHero />
            <MessageLog role="log" aria-live="off" aria-label={__('Chat messages')}>
                {error && (
                    <ChatLoadErrorBanner
                        errorCode={errorCode}
                        onRetry={onReload}
                        reloadBudgetExhausted={reloadBudgetExhausted}
                    />
                )}

                {!error && messages.length === 0 && (
                    <Typography fontSkin="body-standard">{__('No messages yet.')}</Typography>
                )}

                {!error &&
                    messages.map((message) => (
                        <MessageBubble
                            key={message.key}
                            message={message}
                            animate={message.key === latestReplyKey}
                            onCtaLinkClick={onCtaLinkClick}
                        />
                    ))}

                {!error && sendErrorBanner}

                {/*
                 * The waiting state is the pulsing avatar alone — no "typing" caption,
                 * matching the pilot. Screen reader users are told via ChatStatusRegion,
                 * which is why this can stay purely visual.
                 */}
                {awaitingReply && (
                    <FlexBox aria-hidden="true" justifyContent="flex-start" mb={5}>
                        <ChatAssistantAvatar animated />
                    </FlexBox>
                )}

                <div ref={endOfThreadRef} />
            </MessageLog>
        </ThreadScrollArea>
    );
}
